/**
 * Calculation utilities for the Applications Overview.
 */

/**
 * Calculate the sum of a numeric property across an array of objects
 * @param items - Array of objects containing numeric values
 * @param key - Key of the numeric property to sum
 * @returns Total sum of the property values
 */
export const calculateTotal = <T extends Record<string, any>>(
  items: T[],
  key: keyof T
): number => {
  return items.reduce((sum, item) => {
    const value = item[key];
    const numValue = typeof value === "number" ? value : parseFloat(value) || 0;
    return sum + numValue;
  }, 0);
};

/**
 * Calculate percentage with configurable decimal places
 * Matches backend calculation: (part / total) * 100
 * @param part - The part value (numerator)
 * @param total - The total value (denominator)
 * @param decimals - Number of decimal places (default: 1)
 * @returns Percentage as a number with specified decimal places
 */
const calculatePercentage = (
  part: number,
  total: number,
  decimals: number = 1
): number => {
  if (total === 0) return 0;
  return parseFloat(((part / total) * 100).toFixed(decimals));
};

/**
 * Calculate percentages for an array of items and add percentage property
 * @param items - Array of items with a count property
 * @param countKey - Key of the count property (default: "count")
 * @param total - Optional pre-calculated total. If not provided, will be calculated
 * @returns Array of items with percentage property added
 */
export const addPercentages = <T extends Record<string, any>>(
  items: T[],
  countKey: keyof T = "count" as keyof T,
  total?: number
): Array<T & { percentage: number }> => {
  const calculatedTotal = total ?? calculateTotal(items, countKey);

  return items.map((item) => ({
    ...item,
    percentage: calculatePercentage(
      typeof item[countKey] === "number"
        ? item[countKey]
        : parseFloat(item[countKey]) || 0,
      calculatedTotal
    ),
  }));
};
